'use client'

// DiskTerminal: the screen that opens when he puts a floppy disk from his vending machine in the drive at the cafe.
//
// Sam, 29 Oct: "this should open up a new interface on the screen when he loads one - similar to the internet browser -
// but make it more terminal looking. And it should show whats on it, and what Pino is doing with it in realtime".
// It sits where the browser window does (BrowsePopup.jsx) and is the same cream window, but the inside is all terminal,
// in the colours his talks with his friend already use there. What it says is lib/world/diskScreen.js, from the steps
// the town sends (sim world/disks/Screen.js): the disk going in, what is on it, the run, the link. A fixed size, so
// nothing on the page moves as the lines land; they scroll inside it.

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import useGameStore from '../../stores/gameStore'
import { ui, kawaii } from '../../lib/ui/theme'
import { BIRD_NAME } from '../../lib/world/identity'
import { linesOf, statusOf, overOf, catchUpOf, stillMissing } from '../../lib/world/diskScreen'

const TERM = {
  background: '#1c1715',
  color: '#f7e7d2',
  fontFamily: 'var(--font-geist-mono), ui-monospace, monospace',
}
const PROMPT = '#f0b064'
const HIM = '#a8d6a0'
const ERR = '#f29b8f'

const STEP_MS = 40            // a line lands every this long
const LINGER_MS = 40_000      // how long the run stays up to be read once it is over
const GRACE_MS = 15_000       // up from the computer with the disk not run yet: a run may still be on its way
const GONE_MS = 8_000         // after "got up" with nothing run
const STALE_MS = 6 * 60_000   // a sitting caught up on later than this is not shown
const RANK = { in: 0, ran: 1, done: 2, broken: 2 }

function Floppy({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path d="M2 1.5h10l2.5 2.5v10.5H2z" fill={kawaii.ink} />
      <rect x="4.5" y="1.5" width="6" height="4" fill={kawaii.cream} />
      <rect x="8.2" y="2.2" width="1.4" height="2.6" fill={kawaii.ink} />
      <rect x="4" y="8.5" width="8" height="5" rx="0.6" fill={kawaii.cream} />
    </svg>
  )
}

function Line({ l, id, onPicMissing, picMissing }) {
  if (l.k === 'cmd') return <div><span style={{ color: PROMPT }}>A:\&gt;</span> {l.t}</div>
  if (l.k === 'him') return <div style={{ color: HIM }}>{l.t}</div>
  if (l.k === 'what') return <div style={{ margin: '2px 0' }}>{l.t}</div>
  // a line of code is one row, cut at the edge as a terminal listing is, so the disk's top lines stay in view
  if (l.k === 'code') return <div style={{ opacity: 0.55, whiteSpace: 'pre', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.t || ' '}</div>
  if (l.k === 'more') return <div style={{ opacity: 0.55 }}>{l.t}</div>
  if (l.k === 'err') return <div style={{ color: ERR }}>{l.t}</div>
  if (l.k === 'link') return <div style={{ color: HIM }}>{l.t} <a href={l.url} target="_blank" rel="noreferrer" style={{ color: PROMPT, textDecoration: 'underline' }}>open ↗</a></div>
  if (l.k === 'pic') {
    return (
      <div style={{ margin: '6px 0 4px' }}>
        {/* the box is the picture's size before it loads, so nothing moves when it does */}
        <div style={{ width: 168, height: 168, border: '1px solid rgba(247,231,210,0.25)', background: '#000' }}>
          {!picMissing && (
            // (a plain img: one small picture a run, cached for good by its id; the optimiser would only copy it)
            // eslint-disable-next-line @next/next/no-img-element
            <img src={`/api/disk-screen/frame?id=${id}`} width={168} height={168} alt="four moments of the disk running" onError={onPicMissing} style={{ display: 'block', imageRendering: 'pixelated' }} />
          )}
        </div>
        <div style={{ opacity: 0.55 }}>{picMissing ? 'the picture could not be fetched' : l.t}</div>
      </div>
    )
  }
  return <div style={{ opacity: 0.85 }}>{l.t || ' '}</div>
}

export function DiskTerminal() {
  const screen = useGameStore((s) => s.diskScreen)
  const setDiskScreen = useGameStore((s) => s.setDiskScreen)
  const clearBrowseState = useGameStore((s) => s.clearBrowseState)
  const browsing = useGameStore((s) => !!s.browseState)
  const atDisk = useGameStore((s) => s.npcStates?.[0]?.action === 'DISK')

  const [shown, setShown] = useState(0)
  const [left, setLeft] = useState(false)
  const [gone, setGone] = useState(null)       // the id of a sitting that has closed, by its time or the ✕
  const [picMissing, setPicMissing] = useState(false)
  const bodyRef = useRef(null)
  const stick = useRef(true)

  const id = screen && !screen.partial ? screen.id : null
  const lines = useMemo(() => {
    const ls = linesOf(screen, { name: BIRD_NAME, left })
    return ls.map((l) => (l.k === 'link' ? { ...l, url: screen.url } : l))
  }, [screen, left])

  // Catching up, asked of the town only when it is missing (catchUpOf): he has sat down at the drive and no disk came in
  // on the socket (somebody who came in halfway, or a dropped socket), or a step came for a sitting we never saw open.
  const asked = useRef(null)
  const wasAtDisk = useRef(false)
  useEffect(() => {
    function catchUp() {
      fetch('/api/disk-screen', { cache: 'no-store' })
        .then((r) => r.json())
        .then((got) => {
          if (!got?.on || !got.id || Date.now() - (got.at || 0) > STALE_MS) return
          const { on, ...was } = got
          const cur = useGameStore.getState().diskScreen
          const newer = cur?.id === was.id && !cur.partial && RANK[cur.phase] > RANK[was.phase]
          setDiskScreen({ ...was, ...(newer ? cur : {}), partial: false, caught: true })
        })
        .catch(() => { /* no screen, then */ })
    }
    const sat = atDisk && !wasAtDisk.current
    wasAtDisk.current = atDisk
    const ask = catchUpOf(screen, { sat })
    if (ask === 'now' && asked.current !== screen.id) {
      asked.current = screen.id
      catchUp()
    }
    if (ask !== 'soon') return
    const before = screen?.id || null
    let fired = false
    const t = setTimeout(() => {
      fired = true
      if (stillMissing(useGameStore.getState().diskScreen, before)) catchUp()
    }, 2000)
    // (a sitting down still being waited on is waited on again: React runs this twice on its first mount in dev)
    return () => { clearTimeout(t); if (!fired) wasAtDisk.current = false }
  }, [screen, atDisk, setDiskScreen])

  // A new sitting: start the lines from the top (all at once when caught up), and take the browser window down.
  useEffect(() => {
    if (!id) return
    setLeft(false)
    setPicMissing(false)
    stick.current = true
    const s = useGameStore.getState().diskScreen
    const reduced = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    setShown(reduced ? Infinity : s?.caught ? linesOf(s, { name: BIRD_NAME }).length : 0)
    if (s?.phase === 'in' && !s.caught) clearBrowseState()
  }, [id, clearBrowseState])

  // The lines land one at a time, as a terminal prints.
  useEffect(() => {
    if (shown >= lines.length) return
    const t = setTimeout(() => setShown((n) => n + 1), STEP_MS)
    return () => clearTimeout(t)
  }, [shown, lines.length])

  // While the disk is in, held at the top, where it says what he did and what the disk is: the listing goes on below,
  // there to scroll to. Once it runs, at the newest line, unless somebody has scrolled up to read.
  const phase = screen?.phase
  useEffect(() => { stick.current = true }, [phase])
  useLayoutEffect(() => {
    const el = bodyRef.current
    if (el && stick.current && phase !== 'in') el.scrollTop = el.scrollHeight
  }, [shown, lines.length, picMissing, phase])

  // When it closes. Over (run, or would not run): he has got up by then, so say so, and leave it up to be read. Run and
  // up from the computer, with the upload not back: up a while longer, then gone without a word about the upload. Up
  // from the computer with it still in the drive: wait a little for a run on its way, then say he got up and close.
  useEffect(() => {
    if (!id) return
    const timers = []
    if (overOf(screen)) {
      if (!atDisk) setLeft(true)
      timers.push(setTimeout(() => setGone(id), LINGER_MS))
    } else if (phase === 'ran' && !atDisk) {
      timers.push(setTimeout(() => setGone(id), LINGER_MS * 3))
    } else if (phase === 'in' && !atDisk) {
      timers.push(setTimeout(() => {
        setLeft(true)
        timers.push(setTimeout(() => setGone(id), GONE_MS))
      }, GRACE_MS))
    }
    return () => timers.forEach(clearTimeout)
  }, [id, phase, atDisk]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!id || gone === id || browsing) return null

  const status = statusOf(screen, { left })
  const typed = lines.slice(0, shown)
  const done = shown >= lines.length

  return (
    <>
      <style jsx global>{`
        @keyframes dt-in { 0% { opacity: 0; transform: translateX(10px) } 100% { opacity: 1; transform: none } }
        @keyframes dt-pulse { 0%,100% { opacity: 0.55 } 50% { opacity: 1 } }
        @keyframes dt-blink { 0%,49% { opacity: 1 } 50%,100% { opacity: 0 } }
        @media (prefers-reduced-motion: reduce) { .dt-anim { animation: none !important } }
      `}</style>
      {/* on a phone it lies over the "right now" card (LifeNow.jsx), which says the same thing in fewer words while it
          is up; from md, where the browser window sits */}
      <div
        className="fixed z-40 left-3 right-3 top-[224px] md:left-6 md:right-auto md:top-1/2 md:-translate-y-1/2 md:w-[344px] pointer-events-none"
        role="log"
        aria-label={`${BIRD_NAME} running a floppy disk`}
      >
        <div className="dt-anim overflow-hidden pointer-events-auto" style={{ background: kawaii.cream, border: kawaii.outline, borderRadius: 18, boxShadow: kawaii.drop, fontFamily: ui.font, animation: 'dt-in 240ms ease-out' }}>
          <div className="flex items-center gap-2 px-3" style={{ height: 36, borderBottom: kawaii.stitch, background: 'rgba(47,38,80,0.05)' }}>
            <Floppy />
            <span className="text-[12px] truncate" style={{ color: kawaii.ink, fontWeight: 800, minWidth: 0, flex: 1 }}>DISK {screen.no} / {screen.label}</span>
            <span className="flex items-center gap-1.5 shrink-0">
              <span className="dt-anim rounded-full" style={{ width: 7, height: 7, background: status.color, boxShadow: `0 0 6px ${status.color}`, animation: 'dt-pulse 1.6s ease-in-out infinite' }} />
              <span className="text-[11px]" style={{ color: status.color, fontWeight: 700 }}>{status.label}</span>
            </span>
            <button
              type="button"
              onClick={() => setGone(id)}
              aria-label="Close the disk screen"
              className="shrink-0 flex items-center justify-center"
              style={{ width: 24, height: 24, marginRight: -4, borderRadius: 999, color: kawaii.inkSoft, fontSize: 14, fontWeight: 800, lineHeight: 1 }}
            >
              ✕
            </button>
          </div>

          <div
            ref={bodyRef}
            onScroll={(e) => { const el = e.currentTarget; stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24 }}
            className="kawaii-scroll overflow-y-auto"
            style={{ ...TERM, height: 'clamp(150px, calc(100dvh - 440px), 300px)', margin: 8, padding: '8px 10px', borderRadius: 10, border: kawaii.outlineThin, fontSize: 11.5, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', overscrollBehavior: 'contain' }}
          >
            {typed.map((l, i) => (
              <Line key={i} l={l} id={id} picMissing={picMissing} onPicMissing={() => setPicMissing(true)} />
            ))}
            {done && !left && (
              <div><span style={{ color: PROMPT }}>A:\&gt;</span> <span className="dt-anim" style={{ animation: 'dt-blink 1s steps(1) infinite' }}>_</span></div>
            )}
          </div>

          {/* who the idea is from, two lines held whatever its length */}
          <p
            className="px-3 text-[11px] leading-snug"
            style={{ color: kawaii.inkSoft, height: 30, margin: '0 0 9px', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}
            title={screen.credit}
          >
            {screen.credit}
          </p>
        </div>
      </div>
    </>
  )
}

export default DiskTerminal
